import { Button } from '@civilgrid/ui/components';
import { useLocation } from '@tanstack/react-router';
import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import type { ErrorEvent as MapboxErrorEvent, Map as MapboxMap, MapDataEvent } from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import MapBox, {
  AttributionControl,
  type MapEvent,
  type MapMouseEvent,
  type MapRef,
  type ViewState,
  type ViewStateChangeEvent,
} from 'react-map-gl/mapbox';
import { proxy, useSnapshot } from 'valtio';
import { defaultBasemapStyle } from '~/common/types';
import { handleMapAuthError } from '~/components2/Map/helpers/mapAuthError';
import { getMapBounds, saveBoundsToLocalStorage } from '~/components2/Map/helpers/mapBounds';
import { mapAuthTransformRequest } from '~/components2/Map/helpers/transformRequest';
import getConfig from '~/config';
import { useIsMounted } from '~/hooks/useIsMounted';
import layerStore from '~/store2/layers';
import InfoTooltip from '../InfoTooltip';
// store imports
import { BasicMapStore, EmptyStore, type MapStoreBase } from '.';
import type { MapProps } from './common';
import { identifyOnMapClick } from './identifyOnMapClick';
import LiveSessionOnly from './LiveSessionOnly';
import { MAP_LOCALE } from './mapLocale';
import OutOfRegion from './OutOfRegion';
import PassthroughSurface from './PassthroughSurface';
import { BasemapPicker } from './Widgets/BasemapPicker';
import { FilterTableToMap } from './Widgets/FilterTableToMap';
import HighlightLayer from './Widgets/HighlightLayer';
import HistoricalImagery from './Widgets/HistoricalImagery';
import historicalImageryStore from './Widgets/HistoricalImagery/store';
import { GeoreferencedMapsButton, Layers, TOC } from './Widgets/Layers';
import GeoreferencedMapsPanel from './Widgets/Layers/GeoreferencedMapsPanel';
import MetadataPicker from './Widgets/Layers/MetadataPicker';
import { Legend, LegendButton } from './Widgets/Legend';
import MapContextMenu from './Widgets/MapContextMenu/MapContextMenu';
import MapLayers from './Widgets/MapLayers';
import MapSelectionStatus from './Widgets/MapSelectionStatus';
import Measure from './Widgets/Measure';
import MetadataSidebar, { METADATA_SIDEBAR_WIDTH } from './Widgets/MetadataSidebar/MetadataSidebar';
import { isMetadataSidebarOpen } from './Widgets/MetadataSidebar/metadataSidebar.helpers';
import Print from './Widgets/Print/Print';
// Map Widgets
import ProjectLayer from './Widgets/ProjectLayer';
import ReportProblem from './Widgets/ReportProblem';
import Rotation from './Widgets/Rotation';
import Scale from './Widgets/Scale';
import SplitView from './Widgets/SplitView';
import splitViewStore from './Widgets/SplitView/store';
import Zoom from './Widgets/Zoom';

// IMPORTANT: This configures the available layers for the map.
const config = getConfig();

// initial state for the viewState to match MapBox's ViewState typing.
const initialMapViewState: ViewState = {
  longitude: -98,
  latitude: 39,
  zoom: 4,
  bearing: 0,
  pitch: 0,
  padding: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
};

interface MapViewState {
  viewState: ViewState;
}

// controlling view state from this component to keep it consolidated
// and to have access to it everywhere (i.e. within PrintView)
export const mapState = proxy<MapViewState>({ viewState: initialMapViewState });

function createNewEmptyStore() {
  return proxy(new EmptyStore());
}

function createBasicMapStore() {
  return proxy(new BasicMapStore());
}

export const MapComponent = ({
  id,
  store = createNewEmptyStore(),
  mapStyle,
  initialViewState,
  projectGeometry,
  projectCircle,
  showLayers,
  showLayerDetails = true,
  showLayerPicker,
  showLegend,
  showBasemapPicker,
  showZoomButtons,
  showScale,
  units = 'imperial',
  projectId,
  currentMapId,
  children,
  interactiveLayerIds = [],
  controls,
  measureTool = true,
  allowRotation,
  allowHistoricalImagery = false,
  allowSplitView = false,
  printTool = false,
  includeDefaultContextMenu = true,
  isOutOfRegionDisabled = false,
  projectPolygons,
  showAttribution = true,
  onBasemapChange,
  inNewDownloadMode,
  showFilterTableToMap = false,
  preserveDrawingBuffer = false,
  reuseMaps = true,
  persistViewportInLocalStorage = true,
  allowedBasemapStyles,
}: MapProps) => {
  const location = useLocation();
  const { isTOCVisible, isMetadataPickerVisible, isGeoreferencedMapsPanelVisible } = useSnapshot(layerStore);
  const [activeMap, setActiveMap] = useState<'left' | 'right'>('left');
  const { splitView } = useSnapshot(splitViewStore);
  const { historicalImageryMapIndices } = useSnapshot(historicalImageryStore);
  const [splitViewState, setSplitViewState] = useState<ViewState>(mapState.viewState);
  const isMounted = useIsMounted();
  const [isStyleLoaded, setIsStyleLoaded] = useState(false);
  const [currentStyle, setCurrentStyle] = useState(mapStyle);
  const { identifyDetailsPoint } = useSnapshot(store);
  const metadataSidebarOpen = isMetadataSidebarOpen(identifyDetailsPoint, { showLayerDetails, isStyleLoaded });

  const handleMapClick = useCallback((e: MapMouseEvent) => identifyOnMapClick(e, store), [store]);

  // Callback for BasemapPicker to notify when style change starts
  const handleStyleChangeStart = useCallback(() => {
    setIsStyleLoaded(false);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (id !== undefined) {
      const element = document.getElementById(id);
      const bounding = element?.getBoundingClientRect();
      if (!bounding) return;
      const { top, right, bottom, left, width, height, x, y } = bounding;
      store.setMapBounds({ top, right, bottom, left, width, height, x, y });
    }
  }, [id]);

  useEffect(() => {
    return () => {
      // when map unmounts/changes at all, just reset the split view and historical slider.
      splitViewStore.reset();
      historicalImageryStore.reset();
    };
  }, []);

  useEffect(() => {
    if (mapStyle !== currentStyle) {
      setIsStyleLoaded(false);
      setCurrentStyle(mapStyle);
    }
  }, [mapStyle, currentStyle]);

  const mapRef = useCallback((map: MapRef) => {
    if (map) {
      map.resize();
      map.getMap().dragRotate.disable();

      const mapInstance = map.getMap();

      const handleStyleLoad = () => {
        setIsStyleLoaded(true);
      };

      mapInstance.on('style.load', handleStyleLoad);

      const handleDataLoading = (e: MapDataEvent) => {
        if (e.dataType === 'style') {
          setIsStyleLoaded(false);
        }
      };
      mapInstance.on('dataloading', handleDataLoading);

      // Backup: check on idle in case style.load doesn't fire
      mapInstance.on('idle', () => {
        if (mapInstance.isStyleLoaded()) {
          setIsStyleLoaded(true);
        }
      });

      // Check if style is already loaded (in case ref callback runs after initial load)
      if (mapInstance.isStyleLoaded()) {
        setIsStyleLoaded(true);
      }

      map.on('styleimagemissing', (e) => {
        const imageSrc = e.id;
        if (imageSrc === null) {
          return null;
        }
        map.loadImage(imageSrc, (err, image) => {
          if (err) {
            console.error('failed loading dynamic image', err);
            return;
          }
          if (!image) {
            console.error('failed loading dynamic image', err);
            return;
          }
          if (!map.hasImage(imageSrc)) {
            map.addImage(imageSrc, image);
          }
        });
      });

      map.on('styledata', (e) => {
        // NOTE: When migrating to Mapbox GL JS v3, the following may be replaced with `map.setConfigProperty(basemap, 'showPointOfInterestLabels', false);`
        // https://docs.mapbox.com/mapbox-gl-js/guides/migrate-to-v3/#the-mapbox-standard-style
        // example: https://docs.mapbox.com/mapbox-gl-js/example/set-config-property/

        // Layer ids found within, e.target.style.stylesheet.layers
        const layerIdsToHide = ['poi-label', 'transit-label', 'airport-label'];
        layerIdsToHide.forEach((layer) => {
          if (e.target.getLayer(layer)) {
            e.target.setLayoutProperty(layer, 'visibility', 'none');
          }
        });
      });

      return () => {
        mapInstance.off('style.load', handleStyleLoad);
        mapInstance.off('dataloading', handleDataLoading);
      };
    }
  }, []);

  // Using useMemo to execute on initial render to populate initialViewState. useEffect executes after the first render which is too late.
  const storedMapBounds = useMemo(() => {
    if (!persistViewportInLocalStorage || !currentMapId) return;
    return getMapBounds(currentMapId);
  }, [persistViewportInLocalStorage, currentMapId]);

  const resolvedInitialViewState = useMemo(() => {
    if (persistViewportInLocalStorage && storedMapBounds) {
      return { bounds: storedMapBounds };
    }
    return initialViewState;
  }, [persistViewportInLocalStorage, storedMapBounds, initialViewState]);

  const onMapLoad = (e: any) => {
    const map = e.target as MapboxMap;
    const viewState: ViewState = {
      longitude: map.getCenter().lng,
      latitude: map.getCenter().lat,
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
      padding: map.getPadding(),
    };
    mapState.viewState = viewState;
    setSplitViewState(viewState);
  };

  const onMapViewChange = (e: ViewStateChangeEvent) => {
    mapState.viewState = e.viewState;

    // potential memory leak here without isMounted()
    if (isMounted()) {
      setSplitViewState(e.viewState);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const mapLayers = useMemo(
    () => (
      <>
        <HighlightLayer />
        {projectGeometry && <ProjectLayer geometry={projectGeometry} circle={projectCircle} />}
        {showLayers && <MapLayers projectId={projectId} />}
      </>
    ),
    [id, projectGeometry, showLayers, splitView, projectId],
  );

  const renderLayers = isStyleLoaded ? <LiveSessionOnly>{mapLayers}</LiveSessionOnly> : null;
  const layerDetailsPanel = showLayerDetails && isStyleLoaded && <MetadataSidebar store={store} />;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const topLeftSection = useMemo(
    () => (
      <>
        {/* The Layers and Geo-referenced Maps panels open on top of these controls, so they leave the tab order while one is open. */}
        <div
          className="pointer-events-auto absolute left-4 top-4 z-20 flex space-x-2"
          inert={showLayerPicker && (isTOCVisible || isGeoreferencedMapsPanelVisible)}
        >
          <div className="flex flex-col space-y-2">
            {showLayerPicker && <Layers />}
            {showLayerPicker && <GeoreferencedMapsButton />}
            {inNewDownloadMode === true && <DownloadPreview />}
            {controls?.topLeft}
          </div>
        </div>
        {showLayerPicker && isTOCVisible && <TOC />}
        {showLayerPicker && isMetadataPickerVisible && <MetadataPicker />}
        {showLayerPicker && isGeoreferencedMapsPanelVisible && <GeoreferencedMapsPanel />}
      </>
    ),
    [id, isTOCVisible, isMetadataPickerVisible, isGeoreferencedMapsPanelVisible, showLayerPicker],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const topRightSection = useMemo(() => {
    return (
      <div
        // Offset to clear the docked metadata sidebar (+16px gutter); stays at the 16px gutter when closed.
        style={{ right: metadataSidebarOpen ? METADATA_SIDEBAR_WIDTH + 16 : 16 }}
        className="pointer-events-auto absolute top-4 z-10 flex space-x-2"
      >
        {controls?.topRight}
        {historicalImageryMapIndices[0] && <HistoricalImagery mapIndex={0} visible={historicalImageryMapIndices[0]} />}
      </div>
    );
  }, [id, historicalImageryMapIndices[0], metadataSidebarOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const bottomRightSection = useMemo(
    () => (
      <>
        {/* Shift left of the metadata sidebar dock instead of unmounting (CG-855). */}
        {showLegend && <LegendButton metadataSidebarOpen={metadataSidebarOpen} />}
        {showLegend && <Legend metadataSidebarOpen={metadataSidebarOpen} />}
      </>
    ),
    [id, showLegend, metadataSidebarOpen],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const bottomCenterSection = useMemo(() => {
    return (
      <div className="pointer-events-auto absolute bottom-8 left-1/2 z-20 -translate-x-1/2 flex flex-col items-center gap-1.5">
        {showFilterTableToMap && <FilterTableToMap />}
      </div>
    );
  }, [id, showFilterTableToMap]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const bottomLeftSection = useMemo(() => {
    const projectPolygonForPrint =
      projectPolygons ||
      ({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', properties: {}, geometry: projectGeometry }],
      } as FeatureCollection<Polygon | MultiPolygon>);

    return (
      <div className="pointer-events-auto absolute bottom-14 left-4 z-10 flex flex-col items-start p-1.5 gap-2 bg-white rounded-lg border border-grey-30">
        {allowRotation && <Rotation />}
        {measureTool && <Measure store={createBasicMapStore()} />}
        {!inNewDownloadMode && <SplitView visible={allowSplitView} />}
        {printTool && <Print projectPolygons={projectPolygonForPrint} />}
        {!inNewDownloadMode && (
          <BasemapPicker
            visible={showBasemapPicker}
            style={mapStyle}
            onChange={onBasemapChange}
            onStyleChangeStart={handleStyleChangeStart}
            allowHistoricalImagery={allowHistoricalImagery}
            mapIndex={0}
            disabled={!isStyleLoaded}
            allowedBasemapStyles={allowedBasemapStyles}
          />
        )}
        {showZoomButtons && <Zoom />}
        {showScale && units && <Scale units={units} />}
      </div>
    );
  }, [
    id,
    projectPolygons,
    mapStyle,
    handleStyleChangeStart,
    allowRotation,
    measureTool,
    inNewDownloadMode,
    allowSplitView,
    printTool,
    projectGeometry,
    showBasemapPicker,
    onBasemapChange,
    allowHistoricalImagery,
    showZoomButtons,
    showScale,
    units,
    isStyleLoaded,
    allowedBasemapStyles,
  ]);

  const onMove = (e: ViewStateChangeEvent) => {
    setSplitViewState(e.viewState);

    // track in global state too.
    mapState.viewState = e.viewState;
  };

  // A stale session fails one tile request per layer source, which surfaces the dialog long
  // before TokenValidator's next poll.
  const onMapError = useCallback((e: MapboxErrorEvent) => {
    if (!handleMapAuthError(e.error)) {
      console.error(e.error);
    }
  }, []);

  const style = { font: '14px "Noto Sans", sans-serif', borderRadius: '7px' };
  // Match the top-right controls' offset so attribution aligns with them when the sidebar is open.
  const attributionStyle = { fontSize: 12, marginRight: metadataSidebarOpen ? METADATA_SIDEBAR_WIDTH + 16 : 0 };

  if (splitView === true) {
    return (
      <div className="relative flex h-full w-full justify-between">
        <div className="relative w-1/2">
          <MapBox
            preserveDrawingBuffer={location.pathname.toLowerCase().includes('headless')}
            reuseMaps
            ref={mapRef}
            id="splitViewLeft"
            {...splitViewState}
            initialViewState={splitViewState}
            mapStyle={mapStyle || defaultBasemapStyle}
            mapboxAccessToken={config.mapboxToken}
            locale={MAP_LOCALE}
            transformRequest={mapAuthTransformRequest}
            onError={onMapError}
            onMoveStart={() => setActiveMap('left')}
            onMove={activeMap === 'left' ? onMove : undefined}
            onClick={handleMapClick}
            style={style}
            attributionControl={false}
          >
            {renderLayers}
            {layerDetailsPanel}
            {topLeftSection}
            {topRightSection}
            {bottomCenterSection}
            {bottomLeftSection}
            <ContextMenu
              visible={includeDefaultContextMenu}
              projectId={projectId}
              currentMapId={currentMapId}
              store={store}
            />
            <ReportProblem store={store} projectId={projectId} />
            {isStyleLoaded && <LiveSessionOnly>{children}</LiveSessionOnly>}

            <AttributionControl style={attributionStyle} />
          </MapBox>
        </div>

        <div className="absolute left-1/2 top-0 z-10 h-full -translate-x-1/2 border-2 text-primary" />

        <div className="relative w-1/2">
          <MapBox
            reuseMaps
            id="splitViewRight"
            {...splitViewState}
            mapStyle={mapStyle || defaultBasemapStyle}
            mapboxAccessToken={config.mapboxToken}
            locale={MAP_LOCALE}
            transformRequest={mapAuthTransformRequest}
            onError={onMapError}
            onMoveStart={() => setActiveMap('right')}
            onMove={activeMap === 'right' ? onMove : undefined}
            style={style}
            attributionControl={false}
          >
            {renderLayers}
            {allowRotation && <Rotation visible={false} />}
            <div className="pointer-events-auto absolute left-4 top-4">
              <Button variant="secondary" onClick={() => splitViewStore.setSplitView(false)}>
                Exit Split View
              </Button>
            </div>
            <div className="pointer-events-auto absolute bottom-14 left-4 flex flex-col items-start space-y-2 pb-4">
              <BasemapPicker
                visible={showBasemapPicker}
                style={mapStyle}
                onStyleChangeStart={handleStyleChangeStart}
                allowHistoricalImagery={allowHistoricalImagery}
                mapIndex={1}
                disabled={!isStyleLoaded}
              />
            </div>

            <div className="pointer-events-auto absolute right-4 top-4 z-10 flex space-x-2">
              {historicalImageryMapIndices[1] && (
                <HistoricalImagery mapIndex={1} visible={historicalImageryMapIndices[1]} />
              )}
            </div>
            {bottomRightSection}
            {!isOutOfRegionDisabled && <OutOfRegion />}
            {isStyleLoaded && <LiveSessionOnly>{children}</LiveSessionOnly>}
            <AttributionControl style={{ fontSize: 12 }} />
          </MapBox>
        </div>
        <MapSelectionStatus store={store} />
      </div>
    );
  }

  return (
    <MapBox
      reuseMaps
      id={id}
      ref={mapRef}
      mapStyle={mapStyle || defaultBasemapStyle}
      initialViewState={resolvedInitialViewState}
      mapboxAccessToken={config.mapboxToken}
      locale={MAP_LOCALE}
      transformRequest={mapAuthTransformRequest}
      onError={onMapError}
      preserveDrawingBuffer={preserveDrawingBuffer}
      onRender={(e: MapEvent) => e.target.resize()}
      onLoad={onMapLoad}
      onMove={onMapViewChange}
      onZoom={onMapViewChange}
      onRotate={onMapViewChange}
      onClick={handleMapClick}
      keyboard={false}
      interactiveLayerIds={interactiveLayerIds}
      attributionControl={false} // use custom attribution control below to control font size
      style={{
        font: '14px "Noto Sans", sans-serif', // !! this resets the default font size for all children of the map. Without this, the base font size would be defined by whatever mapbox has(12px)
        borderRadius: '7px',
      }}
      onZoomEnd={(e) => {
        if (persistViewportInLocalStorage) {
          saveBoundsToLocalStorage({ e, currentMapId });
        }
      }}
      onDragEnd={(e) => {
        if (persistViewportInLocalStorage) {
          saveBoundsToLocalStorage({ e, currentMapId });
        }
      }}
      maxZoom={24}
    >
      {renderLayers}
      {layerDetailsPanel}
      {topLeftSection}
      {topRightSection}
      {bottomRightSection}
      {bottomCenterSection}
      {bottomLeftSection}
      <ContextMenu
        visible={includeDefaultContextMenu}
        projectId={projectId}
        currentMapId={currentMapId}
        store={store}
      />
      <ReportProblem store={store} projectId={projectId} />

      {!isOutOfRegionDisabled && <OutOfRegion />}

      {isStyleLoaded && <LiveSessionOnly>{children}</LiveSessionOnly>}
      {showAttribution && <AttributionControl style={attributionStyle} />}
      <MapSelectionStatus store={store} />
    </MapBox>
  );
};

const ContextMenu = ({
  store,
  projectId,
  currentMapId,
  visible,
}: {
  store: MapStoreBase;
  projectId?: string;
  currentMapId?: string;
  visible?: boolean;
}) => {
  if (!visible) {
    return null;
  }

  return (
    <MapContextMenu store={store} projectId={projectId} currentMapId={currentMapId}>
      <PassthroughSurface store={store} />
    </MapContextMenu>
  );
};

const DownloadPreview = () => {
  return (
    <div className="flex flex-row space-x-2 rounded-full border border-secondary bg-white px-4 py-2">
      <div>Preview</div>
      <InfoTooltip
        label="About download preview"
        text={`This is a preview of what your selected layers will look like in
        your target download format. You may need to zoom in to see all your
        selected layers.`}
      />
    </div>
  );
};
